/**
 * Usage + cost model for Flow jobs.
 *
 * Back-of-envelope rates, computed from the client-side job snapshot so
 * the dashboard can show realistic numbers today. Phase 1d replaces this
 * with a real /api/usage rollup backed by Application Insights metrics +
 * Azure cost-management exports.
 */
import type { Job } from "@/lib/mockData";

// ──────────────────────────────────────────────────────────────
// Rates (tweak in one place)
// ──────────────────────────────────────────────────────────────

// Per-phase token estimate. A phase is one LLM turn: screenshot + page
// text in, a JSON action out.
const TOKENS_INPUT_PER_PHASE = 8_000;
const TOKENS_OUTPUT_PER_PHASE = 1_000;

// Azure OpenAI GPT-5.4 list price, $/1M tokens.
const PRICE_INPUT_PER_1M = 3.0;
const PRICE_OUTPUT_PER_1M = 15.0;

// Runner VM (Windows B2s spot) — $/hour, fully loaded.
const RUNNER_USD_PER_HOUR = 0.11;

// Supervisor fully-loaded cost, for the "time saved" valuation.
// Back-office ops ~$32/hr fully loaded; supervisors higher. Using $45/hr
// as a defensible midpoint — the figure below is what replaces manual
// labor, not what Flow itself costs.
const SUPERVISOR_USD_PER_HOUR = 45;

// Minutes a human would take to finish the same workflow manually.
// Informs both the "time saved" and the $ ROI.
const MANUAL_MINUTES_BY_WORKFLOW: Record<string, number> = {
  "pipa-referral": 14,
  "anthem-elig": 8,
  "bluecross-auth": 18,
  "uhc-elig": 10,
};
const MANUAL_MINUTES_DEFAULT = 10;

// ──────────────────────────────────────────────────────────────
// Derived per-job numbers
// ──────────────────────────────────────────────────────────────

export interface JobCost {
  /** LLM tokens consumed (input + output). */
  tokens: number;
  /** Runner VM minutes consumed. */
  runnerMinutes: number;
  /** Flow's own cost, $. */
  flowUsd: number;
  /** What the same work would've cost if a human had done it, $. */
  manualUsd: number;
  /** Net savings, $ — positive means Flow was cheaper. */
  savingsUsd: number;
  /** Human minutes saved. */
  minutesSaved: number;
}

/**
 * Did a runner ever pick this job up?
 *
 * `queued` jobs have not run; completed jobs have. For the rest, a
 * `startedAt` of `null` means no (failed to dispatch, or cancelled
 * while still in the queue). `startedAt` absent means the API did not
 * say, so the status is all there is to go on.
 */
export function didRun(job: Pick<Job, "status" | "startedAt">): boolean {
  if (job.status === "queued") return false;
  if (job.status === "success" || job.status === "human") return true;
  return job.startedAt !== null;
}

/**
 * Runner seconds for one job. Zero unless the job actually ran.
 *
 * When both real timestamps are present the duration is taken from
 * them, so a finished job can never be measured "until now". Otherwise
 * it is the API's `elapsed`, which follows the same rule server-side
 * (`_run_seconds` in apps/api/flow_api/routers/jobs.py) and is the only
 * source for a job that is running right now.
 */
export function runSeconds(
  job: Pick<Job, "status" | "elapsed" | "startedAt" | "completedAt">,
): number {
  if (!didRun(job)) return 0;
  if (job.startedAt && job.completedAt) {
    const ms = Date.parse(job.completedAt) - Date.parse(job.startedAt);
    if (Number.isFinite(ms)) return Math.max(0, Math.floor(ms / 1000));
  }
  return Number.isFinite(job.elapsed) ? Math.max(0, job.elapsed) : 0;
}

const NO_COST: JobCost = {
  tokens: 0,
  runnerMinutes: 0,
  flowUsd: 0,
  manualUsd: 0,
  savingsUsd: 0,
  minutesSaved: 0,
};

export function costForJob(job: Job): JobCost {
  // A job no runner picked up used no tokens and no VM time.
  if (!didRun(job)) return NO_COST;

  const phasesRun = Math.max(1, job.curPhase + 1);
  const tokens = phasesRun * (TOKENS_INPUT_PER_PHASE + TOKENS_OUTPUT_PER_PHASE);
  const runnerMinutes = runSeconds(job) / 60;

  const inputUsd = (phasesRun * TOKENS_INPUT_PER_PHASE * PRICE_INPUT_PER_1M) / 1_000_000;
  const outputUsd = (phasesRun * TOKENS_OUTPUT_PER_PHASE * PRICE_OUTPUT_PER_1M) / 1_000_000;
  const runnerUsd = (runnerMinutes / 60) * RUNNER_USD_PER_HOUR;
  const flowUsd = inputUsd + outputUsd + runnerUsd;

  const manualMinutes = MANUAL_MINUTES_BY_WORKFLOW[job.workflow.id] ?? MANUAL_MINUTES_DEFAULT;
  const manualUsd = (manualMinutes / 60) * SUPERVISOR_USD_PER_HOUR;

  // Only count savings for work Flow actually completed.
  const completed = job.status === "success" || job.status === "human";
  const minutesSaved = completed ? Math.max(0, manualMinutes - runnerMinutes) : 0;
  const savingsUsd = completed ? Math.max(0, manualUsd - flowUsd) : 0;

  return { tokens, runnerMinutes, flowUsd, manualUsd, savingsUsd, minutesSaved };
}

// ──────────────────────────────────────────────────────────────
// Dashboard rollup
// ──────────────────────────────────────────────────────────────

export interface UsageRollup {
  /** Every job in the list, whether or not it ran. */
  jobsTotal: number;
  /** Jobs a runner picked up. */
  jobsRun: number;
  /** Jobs that never reached a runner (still queued, or ended before dispatch). */
  jobsNotStarted: number;
  jobsSucceeded: number;
  jobsFailed: number;
  successRate: number;
  runnerMinutes: number;
  tokens: number;
  flowUsd: number;
  manualUsd: number;
  savingsUsd: number;
  hoursSaved: number;
}

export function rollupUsage(jobs: Job[]): UsageRollup {
  let runnerMinutes = 0;
  let tokens = 0;
  let flowUsd = 0;
  let manualUsd = 0;
  let savingsUsd = 0;
  let minutesSaved = 0;
  let succeeded = 0;
  let failed = 0;
  let ran = 0;

  for (const j of jobs) {
    const c = costForJob(j);
    if (didRun(j)) ran += 1;
    runnerMinutes += c.runnerMinutes;
    tokens += c.tokens;
    flowUsd += c.flowUsd;
    minutesSaved += c.minutesSaved;
    savingsUsd += c.savingsUsd;

    // manualUsd is only accrued for work Flow completed — it's the
    // cost avoided, not the cost of every queued ticket.
    if (j.status === "success" || j.status === "human") {
      succeeded += 1;
      manualUsd += c.manualUsd;
    } else if (j.status === "failed") {
      failed += 1;
    }
  }

  const resolved = succeeded + failed;
  const successRate = resolved === 0 ? 0 : succeeded / resolved;

  return {
    jobsTotal: jobs.length,
    jobsRun: ran,
    jobsNotStarted: jobs.length - ran,
    jobsSucceeded: succeeded,
    jobsFailed: failed,
    successRate,
    runnerMinutes,
    tokens,
    flowUsd,
    manualUsd,
    savingsUsd,
    hoursSaved: minutesSaved / 60,
  };
}

// ──────────────────────────────────────────────────────────────
// Formatters
// ──────────────────────────────────────────────────────────────

export function fmtUsd(n: number): string {
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}k`;
  if (n >= 10) return `$${n.toFixed(0)}`;
  return `$${n.toFixed(2)}`;
}

export function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

export function fmtMinutes(n: number): string {
  if (n >= 60) return `${(n / 60).toFixed(1)}h`;
  return `${Math.round(n)}m`;
}

export function fmtPct(n: number): string {
  return `${Math.round(n * 100)}%`;
}
